/** Shared helpers for the Settings screen. */

export const PAGE_SIZE = 50;

const SECTIONS = new Set([
  'workspace', 'notifications', 'team', 'products', 'models', 'billing', 'support',
  'organizations', 'audit', 'activity',
]);

const SECTION_ALIASES = {
  'audit-logs': 'audit',
  'user-activity': 'activity',
};

const EXTERNAL = {
  connectors: '/app/connectors',
  knowledge: '/app/knowledge',
};

/** @param {string|undefined|null} raw */
export function normalizeSection(raw) {
  const key = String(raw || 'team').toLowerCase();
  const section = SECTION_ALIASES[key] || key;
  if (EXTERNAL[section]) return { kind: 'external', path: EXTERNAL[section], section };
  if (SECTIONS.has(section)) return { kind: 'local', section };
  return { kind: 'local', section: 'team' };
}

/**
 * Direct-link gate. Notifications is for every user; audit and activity are admin.
 * @param {string} section
 * @param {object|null|undefined} me
 * @returns {string} Empty when the section may render.
 */
export function sectionDenied(section, me) {
  if (section === 'organizations' && !isSuperAdmin(me)) {
    return 'Only the BonnieOS super admin can open Organizations.';
  }
  if ((section === 'audit' || section === 'activity') && !isAdmin(me)) {
    return 'Only workspace admins can open this.';
  }
  return '';
}

/** @param {object|null|undefined} me */
export function isAdmin(me) {
  return !!(me && me.role === 'admin');
}

/** @param {object|null|undefined} me */
export function isSuperAdmin(me) {
  return isAdmin(me) && me.tenant_slug === 'craftisanlabs';
}

/**
 * @param {object} user
 * @param {Array<{ id: string, name?: string }>} products
 */
export function accessLabel(user, products) {
  if (user && user.product_access === 'specific' && Array.isArray(user.product_ids) && user.product_ids.length) {
    const names = user.product_ids.map((id) => {
      const match = (products || []).find((item) => item.id === id);
      return match ? match.name : id;
    });
    if (names.length <= 2) return names.join(', ');
    return `${names.length} products`;
  }
  return 'All products';
}

/** @param {string} email */
export function nameFromEmail(email) {
  const local = String(email || '').split('@')[0] || 'Member';
  return local
    .replace(/[._+-]+/g, ' ')
    .replace(/\b\w/g, (ch) => ch.toUpperCase())
    .trim() || 'Member';
}

/** @param {string} email */
export function validEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(email || '').trim());
}

/** @param {string} name */
export function initialOf(name) {
  const text = String(name || '').trim();
  return text ? text.charAt(0).toUpperCase() : '?';
}

/**
 * Client-side page of a list (same approach as Schedules).
 * @template T
 * @param {T[]} rows
 * @param {number} shown
 */
export function pageOf(rows, shown) {
  return (rows || []).slice(0, Math.max(0, shown));
}

/** @param {string|undefined|null} raw */
export function roleOf(raw) {
  return raw === 'admin' ? 'admin' : 'member';
}

/**
 * Invite / member product-access body fields.
 * @param {'all'|'specific'|string} access
 * @param {Iterable<string>|Array<string>|Set<string>|undefined|null} productIds
 */
export function accessPayload(access, productIds) {
  if (access === 'specific') {
    const ids = productIds instanceof Set
      ? [...productIds]
      : Array.isArray(productIds) ? [...productIds] : [];
    return { product_access: 'specific', product_ids: ids };
  }
  return { product_access: 'all', product_ids: [] };
}

/**
 * @param {{
 *   email: string,
 *   name?: string,
 *   role?: string,
 *   access?: string,
 *   productIds?: Iterable<string>|Array<string>|Set<string>,
 * }} form
 */
export function invitePayload(form) {
  return {
    email: String(form.email || '').trim().toLowerCase(),
    name: form.name || nameFromEmail(form.email),
    role: roleOf(form.role),
    ...accessPayload(form.access, form.productIds),
  };
}

/**
 * @param {{
 *   name?: string,
 *   role?: string,
 *   active?: boolean,
 *   access?: string,
 *   productIds?: Iterable<string>|Array<string>|Set<string>,
 * }} form
 * @param {{ name?: string }} row
 */
export function memberUpdatePayload(form, row) {
  return {
    name: String(form.name || '').trim() || row.name,
    role: roleOf(form.role),
    is_active: !!form.active,
    ...accessPayload(form.access, form.productIds),
  };
}

/** @param {object|null|undefined} row */
export function productIsArchived(row) {
  return !!(row && row.is_active === false);
}

/** @param {object|null|undefined} row */
export function productStatusChip(row) {
  return productIsArchived(row) ? 'Archived' : ((row && row.status) || 'Active');
}

/** @param {boolean} enabled */
export function modelEnablePayload(enabled) {
  return { enabled: !!enabled };
}

/**
 * Gate for turning a model on — needs a configured provider key.
 * @param {{ configured?: boolean }|null|undefined} model
 * @param {boolean} wantEnabled
 */
export function canEnableModel(model, wantEnabled) {
  if (!wantEnabled) return { ok: true };
  if (model && model.configured === false) {
    return {
      ok: false,
      error: 'Add and test a provider API key before enabling this model',
    };
  }
  return { ok: true };
}

/**
 * Show set / not set only — never echo the hint as if it were the secret.
 * @param {{ key_hint?: string, provider?: string, api_key?: string, value?: string }|null|undefined} key
 */
export function keySetLabel(key) {
  return key && (key.key_hint || key.provider) ? ' · key set' : ' · not set';
}

/** Path for DELETE /models/keys/:provider — never include the secret. */
export function deleteProviderKeyPath(provider) {
  return `/models/keys/${encodeURIComponent(String(provider || '').trim().toLowerCase())}`;
}

/**
 * Body for PUT /models/default. Empty / null clears the org default.
 * @param {string|null|undefined} modelId
 */
export function defaultModelPayload(modelId) {
  const id = modelId == null ? '' : String(modelId).trim();
  return { model_id: id || null };
}

/**
 * Chat picker preselect: local preference wins; else org default; else Auto.
 * @param {{
 *   saved?: string|null,
 *   defaultModelId?: string|null,
 *   optionValues: string[],
 * }} opts
 */
export function resolveChatModelPreselect(opts) {
  const options = opts.optionValues || [];
  const saved = opts.saved == null ? '' : String(opts.saved);
  if (saved && options.includes(saved)) return saved;
  const org = opts.defaultModelId == null ? '' : String(opts.defaultModelId);
  if (org && options.includes(org)) return org;
  return options.includes('auto') ? 'auto' : (options[0] || 'auto');
}
